import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal",
    "testLocationId",
    "openHoursId",
    "fixedSchedule",
    "recurringSchedule",
    "timeInputs",
    "openFrom",
    "openUntil",
    "standardCapacity",
    "accessKeyCapacity"
  ]

  connect() {
    this.form = this.element.querySelector("form");
    this.bootstrapModal = new bootstrap.Modal(this.modalTarget);

    this.standardCapacityTarget.addEventListener("focus", this.selectInputContent.bind(this));
    this.accessKeyCapacityTarget.addEventListener("focus", this.selectInputContent.bind(this));
  }

  selectInputContent(event) {
    event.target.select();
  }

  open(event) {
    const testLocationId = event.currentTarget.dataset.testLocationId
    this.openHoursIdTarget.value = "" // Clear any existing ID

    this.form.reset()
    this.testLocationIdTarget.value = testLocationId

    this.updateFormAction()
    this.bootstrapModal.show()
  }

  edit(event) {
    const openHours = event.currentTarget.dataset
    this.setIds(openHours)
    this.setScheduleType(openHours)
    this.setOperatingStatus(openHours)
    this.setTimes(openHours)
    this.setCapacities(openHours)
    this.setDateOrDay(openHours)

    this.updateFormAction()
    this.bootstrapModal.show()
  }

  setIds(openHours) {
    this.testLocationIdTarget.value = openHours.testLocationId
    this.openHoursIdTarget.value = openHours.id
  }

  setScheduleType(openHours) {
    const scheduleType = openHours.date ? "fixed" : "recurring"
    const scheduleTypeInput = this.form.querySelector(`input[name="open_hours[schedule_type]"][value="${scheduleType}"]`)
    scheduleTypeInput.checked = true
    this.toggleScheduleType({ target: { value: scheduleType } })
  }

  setOperatingStatus(openHours) {
    let operatingStatus
    if (!openHours.openFrom && !openHours.openUntil) {
      operatingStatus = "closed"
    } else if (openHours.openFrom === "00:00" && openHours.openUntil === "00:00") {
      operatingStatus = "24hours"
    } else {
      operatingStatus = "open"
    }
    const operatingStatusInput = this.form.querySelector(`input[name="open_hours[operating_status]"][value="${operatingStatus}"]`)
    operatingStatusInput.checked = true
    this.toggleOperatingStatus({ target: { value: operatingStatus } })
  }

  setTimes(openHours) {
    if (openHours.openFrom) {
      const [hours, minutes] = openHours.openFrom.split(":")
      const fromHourSelect = this.openFromTarget.querySelector('select[name*="open_from(4i)"]')
      const fromMinuteSelect = this.openFromTarget.querySelector('select[name*="open_from(5i)"]')
      fromHourSelect.value = hours
      fromMinuteSelect.value = minutes
    }

    if (openHours.openUntil) {
      const [hours, minutes] = openHours.openUntil.split(":")
      const untilHourSelect = this.openUntilTarget.querySelector('select[name*="open_until(4i)"]')
      const untilMinuteSelect = this.openUntilTarget.querySelector('select[name*="open_until(5i)"]')
      untilHourSelect.value = hours
      untilMinuteSelect.value = minutes
    }
  }

  setCapacities(openHours) {
    this.standardCapacityTarget.value = openHours.standardCapacity || 0
    this.updateCapacity({ target: this.standardCapacityTarget })
    this.accessKeyCapacityTarget.value = openHours.accessKeyCapacity || 0
  }

  setDateOrDay(openHours) {
    const scheduleType = openHours.date ? "fixed" : "recurring"
    if (scheduleType === "fixed") {
      const dateInput = this.fixedScheduleTarget.querySelector("input")
      dateInput.value = openHours.date
    } else {
      const daySelect = this.recurringScheduleTarget.querySelector("select")
      daySelect.value = openHours.day
    }
  }

  updateFormAction() {
    const basePath = this.form.dataset.basePath
    const openHoursId = this.openHoursIdTarget.value
    const path = openHoursId ? `${basePath}/${openHoursId}` : basePath
    this.form.action = path

    // Remove any existing _method input
    const existingMethodInput = this.form.querySelector('input[name="_method"]')
    if (existingMethodInput) {
      existingMethodInput.remove()
    }

    // Add _method input for Rails method override
    if (openHoursId) {
      const methodInput = document.createElement('input')
      methodInput.type = 'hidden'
      methodInput.name = '_method'
      methodInput.value = 'put'
      this.form.appendChild(methodInput)
    }
  }

  toggleScheduleType(event) {
    const isFixed = event.target.value === "fixed";
    this.fixedScheduleTarget.classList.toggle("d-none", !isFixed);
    this.recurringScheduleTarget.classList.toggle("d-none", isFixed);
  }

  toggleOperatingStatus(event) {
    const status = event.target.value;
    const timeInputs = this.timeInputsTarget;
    const openFrom = this.openFromTarget;
    const openUntil = this.openUntilTarget;

    const setTimeValues = (fromHour, fromMinute, untilHour, untilMinute) => {
      openFrom.querySelector('select[name*="open_from(4i)"]').value = fromHour;
      openFrom.querySelector('select[name*="open_from(5i)"]').value = fromMinute;
      openUntil.querySelector('select[name*="open_until(4i)"]').value = untilHour;
      openUntil.querySelector('select[name*="open_until(5i)"]').value = untilMinute;
    }

    switch (status) {
      case "open":
        timeInputs.classList.remove("d-none");
        setTimeValues("09", "00", "17", "00");
        break;
      case "24hours":
        timeInputs.classList.add("d-none");
        setTimeValues("00", "00", "00", "00");
        break;
      case "closed":
        timeInputs.classList.add("d-none");
        setTimeValues("", "", "", "");
        break;
    }
  }

  validateTimes(event) {
    const fromHourSelect = this.openFromTarget.querySelector('select[name*="open_from(4i)"]')
    const fromMinuteSelect = this.openFromTarget.querySelector('select[name*="open_from(5i)"]')
    const untilHourSelect = this.openUntilTarget.querySelector('select[name*="open_until(4i)"]')
    const untilMinuteSelect = this.openUntilTarget.querySelector('select[name*="open_until(5i)"]')

    const fromHours = parseInt(fromHourSelect.value)
    const fromMinutes = parseInt(fromMinuteSelect.value)
    const untilHours = parseInt(untilHourSelect.value)
    const untilMinutes = parseInt(untilMinuteSelect.value)

    // Convert to minutes for easier comparison
    const fromTotalMinutes = fromHours * 60 + fromMinutes
    const untilTotalMinutes = untilHours * 60 + untilMinutes

    if (fromTotalMinutes >= untilTotalMinutes) {
      // If opening time is after or equal to closing time, adjust closing time
      const newUntilHours = fromHours + 1
      const newUntilMinutes = 0

      untilHourSelect.value = newUntilHours.toString().padStart(2, '0')
      untilMinuteSelect.value = newUntilMinutes.toString().padStart(2, '0')
    }
  }

  updateCapacity(event) {
    const standardCapacity = parseInt(event.target.value) || 0;
    const accessKeyCapacity = this.accessKeyCapacityTarget;
    accessKeyCapacity.min = standardCapacity;
    if (parseInt(accessKeyCapacity.value) < standardCapacity) {
      accessKeyCapacity.value = standardCapacity;
    }
  }
}
